import React, { CSSProperties } from "react";
import { heroStyles } from "../../../config/styleConfig";

interface HeroExportProps {
  title?: string;
  subtitle?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  backgroundColor?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  minHeight?: number;
  buttonText?: string;
  buttonLink?: string;
  buttonTarget?: "_self" | "_blank";
  buttonLinkType?: string;
  buttonVariant?: "primary" | "secondary" | "outline" | "white";
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonVariant?: "primary" | "secondary" | "outline" | "white";
  textAlign?: "left" | "center" | "right";
  titleColor?: string;
  subtitleColor?: string;
  textFontFamily?: string;
}

export const HeroExport: React.FC<HeroExportProps> = ({
  title = "Welcome to Our Clinic",
  subtitle,
  backgroundImage,
  backgroundPosition = "50% 50%",
  backgroundZoom = 1,
  backgroundColor = "#1e3a5f",
  overlayColor = "#000000",
  overlayOpacity = 0.4,
  minHeight = 500,
  buttonText,
  buttonLink = "#",
  buttonTarget = "_self",
  buttonLinkType,
  buttonVariant = "primary",
  secondaryButtonText,
  secondaryButtonLink = "#",
  secondaryButtonVariant = "outline",
  textAlign = "center",
  titleColor = "#ffffff",
  subtitleColor = "#e5e7eb",
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};
  // Honor the hero button's "open in new tab" setting; internal (section) links stay _self.
  const buttonActualTarget = buttonLinkType && buttonLinkType !== "external" ? "_self" : buttonTarget || "_self";
  const safeMinHeight = minHeight || heroStyles.minHeight.desktop;
  const currentStyles = {
    title: heroStyles.title.desktop,
    subtitle: heroStyles.subtitle.desktop,
    padding: heroStyles.padding.desktop,
    button: heroStyles.button.desktop,
  };

  const getButtonStyles = (variant: string = "primary"): CSSProperties => {
    const baseStyles: CSSProperties = {
      display: "inline-block",
      padding: `${currentStyles.button.py}px ${currentStyles.button.px}px`,
      borderRadius: "8px",
      fontWeight: 500,
      fontSize: "1rem",
      textDecoration: "none",
      transition: "opacity 0.2s",
      border: "none",
    };

    switch (variant) {
      case "white":
        return { ...baseStyles, backgroundColor: "#ffffff", color: "#1f2937" };
      case "outline":
        return { ...baseStyles, backgroundColor: "transparent", border: "2px solid white", color: "#ffffff" };
      case "secondary":
        return { ...baseStyles, backgroundColor: "var(--color-secondary, #10b981)", color: "#ffffff" };
      default:
        return { ...baseStyles, background: "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)", color: "#ffffff" };
    }
  };

  const containerStyle: CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: safeMinHeight,
    backgroundColor: backgroundImage ? "transparent" : backgroundColor,
    overflow: "hidden",
  };

  const bgPosition = backgroundPosition || "50% 50%";

  return (
    <div style={containerStyle} className="hero-section">
      {backgroundImage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: bgPosition,
            backgroundRepeat: "no-repeat",
            transform: `scale(${backgroundZoom || 1})`,
            transformOrigin: bgPosition,
          }}
        />
      )}
      {backgroundImage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: overlayColor,
            opacity: overlayOpacity,
          }}
        />
      )}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: "56rem",
          padding: `${currentStyles.padding.py}px ${currentStyles.padding.px}px`,
          textAlign: textAlign as CSSProperties["textAlign"],
        }}
      >
        <h1
          style={{
            fontSize: currentStyles.title.fontSize,
            fontWeight: currentStyles.title.fontWeight as CSSProperties["fontWeight"],
            color: titleColor,
            marginBottom: "16px",
            marginTop: 0,
            wordWrap: "break-word",
            overflowWrap: "break-word",
            ...ffStyle(title),
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              fontSize: currentStyles.subtitle.fontSize,
              color: subtitleColor,
              marginBottom: "32px",
              marginTop: 0,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              ...ffStyle(subtitle || ""),
            }}
          >
            {subtitle}
          </p>
        )}
        {(buttonText || secondaryButtonText) && (
          <div style={{ display: "flex", gap: "16px", justifyContent: textAlign, flexWrap: "wrap" }}>
            {buttonText && (
              <a
                href={buttonLink}
                target={buttonActualTarget}
                rel={buttonActualTarget === "_blank" ? "noopener noreferrer" : undefined}
                style={getButtonStyles(buttonVariant)}
              >
                {buttonText}
              </a>
            )}
            {secondaryButtonText && (
              <a href={secondaryButtonLink} style={getButtonStyles(secondaryButtonVariant)}>
                {secondaryButtonText}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
